// Program breakdowns for the Preschool Program page (2K, 3K, Pre-K)
export const PRESCHOOL_PROGRAMS = [
  {
    key: "2k",
    label: "2K Program",
    age: "Two-year-olds",
    color: "from-brand-green to-brand-green-deep",
    pill: "bg-brand-green-soft text-brand-green-deep",
    intro: [
      "The Innovators Prep 2K Program provides a warm, nurturing, and developmentally appropriate early learning experience designed specifically for two-year-old children. Our program recognizes that age two is an important period of rapid growth, when children are developing independence, communication skills, social relationships, curiosity, and an understanding of the world around them.",
      "Our 2K classrooms provide a safe, engaging, play-based learning environment where children are encouraged to explore, discover, create, and learn at their own pace. Daily activities are intentionally planned to support the development of the whole child, including social-emotional, language and communication, cognitive, physical, creative, and self-help skills.",
    ],
    listLabel: "Children participate in a variety of age-appropriate experiences, including:",
    items: [
      { title: "Learning Through Play", desc: "Hands-on activities, dramatic play, sensory exploration, blocks, manipulatives, and open-ended materials encourage creativity, problem-solving, and discovery." },
      { title: "Language & Early Literacy", desc: "Stories, songs, conversations, rhymes, and interactive activities help build vocabulary, communication skills, and an early love of books." },
      { title: "Social-Emotional Development", desc: "Children learn to express their feelings, develop relationships, participate in classroom routines, take turns, and begin developing positive interactions with peers and adults." },
      { title: "Early Math & Cognitive Development", desc: "Sorting, matching, counting, puzzles, shapes, colors, and cause-and-effect experiences introduce foundational concepts through play." },
      { title: "Fine & Gross Motor Development", desc: "Art activities, manipulatives, movement, outdoor play, music, and physical activities help children strengthen coordination and motor skills." },
      { title: "Independence & Self-Help Skills", desc: "Daily routines encourage children to develop age-appropriate independence with eating, handwashing, cleaning up, dressing, toileting readiness, and caring for their belongings." },
      { title: "Creative Expression", desc: "Art, music, movement, sensory experiences, and imaginative play provide opportunities for children to express themselves and develop confidence." },
    ],
    outro:
      "Our goal is for every child in the Innovators Prep 2K Program to feel safe, loved, confident, curious, and excited about learning while developing the foundational skills necessary for a successful transition into 3K and future early childhood learning experiences.",
    learnMoreTo: "/programs/preschool/2k",
  },
  {
    key: "3k",
    label: "3K Program",
    age: "Three-year-olds",
    color: "from-brand-purple to-brand-purple-deep",
    pill: "bg-brand-purple-soft text-brand-purple-deep",
    intro: [
      "The Innovators Prep 3K Program supports three-year-old children as they develop greater independence, strengthen communication skills, build relationships, and become active members of a classroom community.",
    ],
    listLabel: "Through play-based and teacher-guided experiences, children participate in activities that promote:",
    items: [
      { title: "Social-Emotional Development", desc: "Building relationships, expressing emotions, developing self-regulation, learning classroom routines, and practicing cooperation with peers." },
      { title: "Language & Early Literacy", desc: "Participating in conversations, listening to stories, singing songs, building vocabulary, exploring books, and developing early literacy awareness." },
      { title: "Early Math & Cognitive Skills", desc: "Counting, sorting, matching, identifying patterns, exploring shapes and quantities, problem-solving, and developing critical-thinking skills." },
      { title: "Science & Discovery", desc: "Exploring nature, experimenting with materials, asking questions, making observations, and learning about the world around them." },
      { title: "Fine & Gross Motor Development", desc: "Strengthening coordination through art, writing experiences, manipulatives, movement, outdoor play, and other physical activities." },
      { title: "Creative Expression", desc: "Encouraging imagination and self-expression through art, music, movement, dramatic play, and sensory experiences." },
      { title: "Independence & Self-Help Skills", desc: "Supporting children as they develop greater responsibility for personal belongings, toileting, dressing, eating, cleaning up, and classroom routines." },
    ],
    outro:
      "Our goal is to help children become confident, curious, independent learners who are prepared to successfully transition into Pre-K.",
  },
  {
    key: "prek",
    label: "Pre-K Program",
    age: "Four-year-olds",
    color: "from-amber-500 to-amber-600",
    pill: "bg-amber-100 text-amber-800",
    intro: [
      "The Innovators Prep Pre-K Program builds upon children's early learning experiences while preparing four-year-old children for the transition to kindergarten. Our classrooms balance purposeful play with intentional teaching to strengthen children's academic, social-emotional, communication, and independence skills.",
    ],
    listLabel: "Children are provided with meaningful opportunities to develop:",
    items: [
      { title: "Early Literacy & Communication", desc: "Strengthening vocabulary, storytelling, listening comprehension, phonological awareness, letter and sound recognition, name writing, and early writing skills." },
      { title: "Early Mathematics", desc: "Developing number sense, counting, comparing quantities, recognizing patterns and shapes, measuring, sorting, and solving age-appropriate mathematical problems." },
      { title: "Social-Emotional Skills", desc: "Developing friendships, resolving conflicts, communicating needs, managing emotions, following routines, and becoming increasingly independent." },
      { title: "Science, Inquiry & Critical Thinking", desc: "Asking questions, making predictions, investigating topics of interest, conducting simple experiments, and discussing observations." },
      { title: "Creative Arts", desc: "Exploring visual art, music, movement, dramatic play, and other opportunities for creative expression." },
      { title: "Physical Development", desc: "Strengthening fine and gross motor abilities through writing, drawing, manipulatives, movement activities, and outdoor play." },
      { title: "Kindergarten Readiness", desc: "Building the confidence, independence, communication skills, classroom behaviors, and foundational academic skills children need for a successful transition to kindergarten." },
    ],
    outro:
      "Our goal is to help children enter kindergarten as confident, independent, curious, and enthusiastic learners.",
  },
];

// Program-specific support content for Our Approach page
export const LEARNER_SUPPORT = [
  {
    key: "2k",
    label: "2K Learners",
    color: "from-brand-green to-brand-green-deep",
    pill: "bg-brand-green-soft text-brand-green-deep",
    heading: "Supporting Our 2K Learners",
    intro:
      "For many of our 2K students, school is one of their first experiences learning and growing outside of the home. We focus on creating a warm, predictable, and nurturing environment where children feel safe developing relationships and exploring their surroundings.",
    listLabel: "We support our youngest learners through:",
    items: [
      "Consistent classroom routines that provide security and predictability",
      "Play-based and sensory-rich learning experiences",
      "Songs, stories, movement, art, and hands-on exploration",
      "Opportunities to develop early language and communication",
      "Support with separation and transitioning into the school environment",
      "Encouragement of early friendships and positive peer interactions",
      "Development of self-help skills, including feeding, dressing, handwashing, toileting readiness, and cleaning up",
      "Individualized attention based on each child's developmental needs",
    ],
    outro:
      "Our goal is to help children become comfortable, confident, curious, and increasingly independent as they begin their educational journey.",
  },
  {
    key: "3k",
    label: "3K Learners",
    color: "from-brand-purple to-brand-purple-deep",
    pill: "bg-brand-purple-soft text-brand-purple-deep",
    heading: "Supporting Our 3K Learners",
    intro:
      "Our 3K students are developing greater independence, communication skills, imagination, and awareness of the world around them. Teachers build upon these emerging abilities through purposeful play and intentional learning experiences.",
    listLabel: "We support our 3K learners through:",
    items: [
      "Purposeful learning centers and hands-on exploration",
      "Early literacy, language, math, science, art, and movement experiences",
      "Opportunities to make choices and solve problems",
      "Support for identifying and expressing emotions",
      "Development of friendships, sharing, cooperation, and turn-taking",
      "Small-group and individualized learning opportunities",
      "Activities that strengthen fine and gross motor development",
      "Encouragement of independence with classroom and personal-care routines",
    ],
    outro:
      "Children are encouraged to ask questions, explore ideas, communicate, create, and develop a positive identity as learners.",
  },
  {
    key: "prek",
    label: "Pre-K Learners",
    color: "from-amber-500 to-amber-600",
    pill: "bg-amber-100 text-amber-800",
    heading: "Supporting Our Pre-K Learners",
    intro:
      "Our Pre-K Program builds upon children's existing skills while preparing them socially, emotionally, academically, and physically for the transition to kindergarten.",
    listLabel: "We support our Pre-K learners through:",
    items: [
      "Developmentally appropriate early literacy and writing experiences",
      "Early mathematics and number-sense activities",
      "Science, discovery, investigation, and problem-solving",
      "Opportunities for creative expression through art, music, movement, and dramatic play",
      "Activities that strengthen attention, communication, and classroom participation",
      "Social-emotional learning and positive approaches to conflict resolution",
      "Opportunities to develop leadership, responsibility, and independence",
      "Individualized support based on children's strengths and developmental progress",
      "Kindergarten-readiness experiences incorporated naturally throughout the school day",
    ],
    outro:
      "Our goal is to help children enter kindergarten as confident, independent, curious, and enthusiastic learners.",
  },
  {
    key: "specialed",
    label: "Special Education",
    color: "from-pink-500 to-rose-500",
    pill: "bg-rose-100 text-rose-700",
    heading: "Supporting Our Special Education Students",
    intro:
      "Our 4410 Special Education Preschool Program recognizes that meaningful progress looks different for every child. We provide individualized instruction and specialized support based on each student's strengths, developmental needs, and Individualized Education Program (IEP).",
    listLabel: "We support our special education students through:",
    items: [
      "Individualized instruction aligned with IEP goals",
      "Small-group and individualized learning opportunities",
      "Structured and predictable classroom routines",
      "Visual, sensory, communication, and behavioral supports when appropriate",
      "Positive strategies that promote self-regulation and successful classroom participation",
      "Opportunities to strengthen communication, social-emotional, cognitive, motor, adaptive, and self-help skills",
      "Purposeful play and meaningful peer interactions",
      "Related services, such as speech-language therapy, occupational therapy, and physical therapy, when mandated by a child's IEP",
      "Collaboration among teachers, paraprofessionals, related service providers, administrators, and families",
      "Ongoing observation and monitoring of children's progress",
      "Support for transitions into future educational settings",
    ],
    outro:
      "Most importantly, we recognize children for what they can do and build upon their strengths while providing the individualized support they need to continue progressing.",
  },
];
